import { AppDispatch } from "app/store";
import OmronFooter from "components/OmronFooter";
import OmronHeader from "components/OmronHeader";
import { selectUserState } from "features/auth";
import {
  changeCode,
  changePassword,
  changePasswordCheck,
  resetExcuteAsync,
  selectCode,
  selectEmail,
  selectPasswordValidationErrors,
  selectPassword,
  selectPasswordCheck,
} from "features/reset";

import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

const ResetExcutePage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const email = useSelector(selectEmail);
  const password = useSelector(selectPassword);
  const passwordConfirm = useSelector(selectPasswordCheck);
  const code = useSelector(selectCode);
  const errors = useSelector(selectPasswordValidationErrors);
  const user = useSelector(selectUserState);
  const [error, SetError] = useState({
    hasErrorEmptyPassword: false,
    hasErrorIncorrectPassword: false,
    hasErrorNotMatchPassword: false,
  });

  const onSubmit = useCallback(async () => {
    SetError(errors);
    if (
      errors.hasErrorEmptyPassword ||
      errors.hasErrorIncorrectPassword ||
      errors.hasErrorNotMatchPassword
    ) {
      return;
    }
    dispatch(resetExcuteAsync());
  }, [errors, dispatch]);

  return (
    <MainWrap>
      <OmronHeader
        link={{ label: t("common.label.breadcrumbsSignin"), path: "/" }}
        label={[t("common.label.breadcrumbsPasswordReset")]}
        user={user}
      />
      <PageMain>
        <InnerDiv>
          <Title>{t("resetPage.label.resetTitle")}</Title>
          <Text>
            {t("resetPage.text.resetSendEMail")}
            <br />
            {email}
          </Text>
          <Text>
            {t("resetPage.text.inputDescription")}
            <br />
            {t("resetPage.text.notSendEmail")}
            <br />
            <Link
              href="https://www.fa.omron.co.jp/product/tool/ss-video-manual/en/licence-help/new-registration/index.html"
              target="_blank"
            >
              {t("resetPage.text.supportEnglish")}
              <br />
            </Link>
            <Link
              href="https://www.fa.omron.co.jp/product/tool/ss-video-manual/ja/licence-help/new-registration/index.html"
              target="_blank"
            >
              {t("resetPage.text.supportJapanese")}
            </Link>
          </Text>
          <section>
            <form>
              <Dl>
                <Dt>
                  {t("resetPage.label.authCode")}
                  <Required>{t("resetPage.label.required")}</Required>
                </Dt>
                <Dd>
                  <CodeInput
                    type="text"
                    size={15}
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      dispatch(changeCode({ code: e.target.value }));
                    }}
                  />
                </Dd>
                <PasswordDt>
                  {t("resetPage.label.newPassword")}
                  <Required>{t("resetPage.label.required")}</Required>
                  <Span>
                    {t("resetPage.label.passwordAllowdCharactoers")}
                    <br />
                    {t("resetPage.label.passwordContainsLowerCase")}
                    <br />
                    {t("resetPage.label.passwordContainsUpperCase")}
                    <br />
                    {t("resetPage.label.passwordContainsNumberAndSymbol")}
                  </Span>
                </PasswordDt>
                <PasswordDd className="u-margin__bottom-2">
                  <PasswordInput
                    type="password"
                    size={40}
                    value={password}
                    isError={
                      error.hasErrorEmptyPassword ||
                      error.hasErrorIncorrectPassword
                    }
                    onChange={(e) => {
                      dispatch(changePassword({ password: e.target.value }));
                    }}
                  />
                  <br />
                  {error.hasErrorEmptyPassword && (
                    <ErrorComment>
                      {t("resetPage.message.requiredEmptyError")}
                    </ErrorComment>
                  )}
                  {error.hasErrorIncorrectPassword && (
                    <ErrorComment>
                      {t("resetPage.message.notAllowedPasswordRuleError")}
                    </ErrorComment>
                  )}
                  <PasswordConfirmInput
                    type="password"
                    size={40}
                    value={passwordConfirm}
                    isError={error.hasErrorNotMatchPassword}
                    onChange={(e) => {
                      dispatch(
                        changePasswordCheck({ passwordCheck: e.target.value })
                      );
                    }}
                    placeholder={t("resetPage.label.confirm")}
                  />
                  {error.hasErrorNotMatchPassword && (
                    <ErrorComment>
                      {t("resetPage.message.notMatchPasswordError")}
                    </ErrorComment>
                  )}
                </PasswordDd>

                <ButtonDd>
                  <Button
                    type="button"
                    value={t("resetPage.label.excuteButton")}
                    onClick={onSubmit}
                  />
                </ButtonDd>
              </Dl>
            </form>
          </section>
        </InnerDiv>
      </PageMain>
      <OmronFooter />
    </MainWrap>
  );
};

export default ResetExcutePage;

const InnerDiv = styled.div`
  width: 1000px;
  margin: 0 auto;
`;
const PageMain = styled.main`
  grid-area: main;
  padding: 3.2rem 50px;
`;
const MainWrap = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-areas: "header" "breadc" "main" "footer";
  min-height: 100vh;
`;

const Title = styled.h1`
  margin-bottom: 3rem;
`;

const Text = styled.p`
  margin-bottom: 3rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  white-space: pre-wrap;
`;

const Link = styled.a`
  color: #005eb8;
  padding-left: 15px;
`;

const Dl = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;

const Dd = styled.dd`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  background: #fafafa;
  width: 70%;
  padding: 1rem 0;
  :nth-of-type(2n) {
    background: #f0f0f0;
  }
`;

const PasswordDd = styled(Dd)`
  margin-bottom: 2rem;
`;

const Dt = styled.dt`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  background: #fafafa;
  width: 26%;
  padding: 1.3rem 2%;
  :nth-of-type(2n) {
    background: #f0f0f0;
  }
`;

const PasswordDt = styled(Dt)`
  margin-bottom: 2rem;
`;

const CodeInput = styled.input`
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  :focus {
    outline: 0;
    background: #fafafa;
  }
`;

const PasswordInput = styled.input<{ isError?: boolean }>`
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  :focus {
    outline: 0;
    background: #fafafa;
  }
  ${({ isError }) =>
    isError &&
    css`
      background: rgba(229, 0, 0, 0.08);
      :focus {
        background: rgba(229, 0, 0, 0.08);
      }
    `}
`;

const PasswordConfirmInput = styled(PasswordInput)`
  margin-top: 1rem;
`;

const Span = styled.span`
  display: block;
  color: #999999;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;

const ButtonDd = styled(Dd)`
  text-align: center;
  width: 100%;
  background: none;
`;

const Button = styled.input`
  min-width: 15rem;
  padding: 1.23rem 2rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  transition: all 0.3s ease-out;
  cursor: pointer;
  opacity: 1;
  color: #ffffff;
  background: #005eb8;
  border: 1px #005eb8 solid;
  pointer-events: auto;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
`;

const ErrorComment = styled.span`
  margin-top: 0.2rem;
  display: block;
  color: #e50000;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;

const Required = styled.p`
  display: inline-block;
  color: #e50000;
  float: right;
  padding-top: 0.3rem;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;
